import DashboardLayout from "reusableComponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "reusableComponents/Navbars/DashboardNavbar";
import Box from "@mui/material/Box";
import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import Pagination from "@mui/material/Pagination";
import { createIssue, getCategories, getIssuesByUser, deleteIssue } from "../../axios-client";
import { FileUploadBox } from "../../components/upload/FileUploadBox";
import CustomSelect from "../../reusableComponents/Select/CustomSelect";
import SnackbarNotification from "../../reusableComponents/Snackbar/SnackbarNotification";
import { useTranslation } from "react-i18next";
import StatusBadge from "../../components/SoftBadge/StatusBadge";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DeleteIcon from "@mui/icons-material/Delete";
import { MdWarning } from "react-icons/md";
import {
    MenuItem,
    Select,
} from "@mui/material";

const ReportIssues = () => {
    const [issues, setIssues] = useState([]);
    const [notification, setNotification] = useState({
        show: false,
        snackbarMessage: "",
        severity: "",
    });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(6);
    const [totalPages, setTotalPages] = useState(1);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openImage, setOpenImage] = useState(null);
    const [imageList, setImageList] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, issueId: null, issueTitle: "" });
    const { t } = useTranslation(["reportIssue"]);

    const initialStateFormData = {
        title: "",
        description: "",
        priority: "medium",
        category_id: "",
        files: [],
    };

    const [formData, setFormData] = useState(initialStateFormData);

    useEffect(() => {
        getCategories().then((res) => {
            const options = res?.data?.map((opt) => ({
                ...opt,
                label: opt.label.charAt(0).toUpperCase() + opt.label.slice(1),
            }));
            setCategoryOptions(options);
        });
    }, []);

    const showNotification = (snackbarMessage, severity) => {
        setNotification({ show: true, snackbarMessage, severity });
    };

    const fetchIssues = async () => {
        getIssuesByUser({ page, pageSize }).then((res) => {
            setIssues(res.data.issues);
            setTotalPages(res.data.totalPages);
        });
    };

    useEffect(() => {
        fetchIssues();
    }, [page, pageSize]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleCustomSelectChange = (name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true);
        const data = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
            if (key === "files") {
                value.forEach((file) => data.append("files", file));
            } else {
                data.append(key, value);
            }
        });

        createIssue(data).then(() => {
            fetchIssues();
            setFormData(initialStateFormData);
            setLoading(false);
        }).catch((e) => {
            console.error(e)
            setLoading(false);
        })
    };

    const handleDeleteIssue = async () => {
        const issueId = deleteConfirm.issueId;
        setDeleteConfirm({ open: false, issueId: null, issueTitle: "" });

        try {
            await deleteIssue(issueId);
            setIssues((prevIssues) => prevIssues.filter((issue) => issue.id !== issueId));
            showNotification("Issue deleted successfully", "success");
        } catch (error) {
            console.error("Failed to delete issue", error);
            showNotification("Failed to delete issue", "error");
        }
    };

    const handleImageClick = (images, index) => {
        setImageList(images);
        setCurrentImageIndex(index);
        setOpenImage(images[index]);
    };

    const handleCloseDialog = () => {
        setOpenImage(null);
        setImageList([]);
        setCurrentImageIndex(0);
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <SoftBox mt={4}>
                <Grid container spacing={3}>
                    {/* FORM */}
                    <Grid item xs={12} md={6}>
                        <Card>
                            <SoftBox p={3}>
                                <SoftTypography variant="h5" fontWeight="bold" gutterBottom>
                                    {t("name")}
                                </SoftTypography>
                                <form onSubmit={handleSubmit}>
                                    <SoftBox mb={2}>
                                        <SoftInput
                                            placeholder={t("title")}
                                            name="title"
                                            value={formData.title}
                                            onChange={handleChange}
                                        />
                                    </SoftBox>
                                    <SoftBox mb={2}>
                                        <SoftInput
                                            placeholder={t("description")}
                                            name="description"
                                            multiline
                                            rows={4}
                                            value={formData.description}
                                            onChange={handleChange}
                                        />
                                    </SoftBox>
                                    <SoftBox mb={2}>
                                        <CustomSelect
                                            fullWidth
                                            value={formData.category_id}
                                            onChange={(val) =>
                                                handleCustomSelectChange("category_id", val)
                                            }
                                            name="category_id"
                                            placeholder={t("selectCategory")}
                                            options={categoryOptions}
                                        />
                                    </SoftBox>
                                    <SoftBox mb={2}>
                                        <FileUploadBox
                                            onFileSelected={(files) =>
                                                setFormData((prev) => ({ ...prev, files }))
                                            }
                                            selectedFile={formData.files}
                                            isMultiple={true}
                                        />
                                    </SoftBox>
                                    <SoftButton
                                        type="submit"
                                        color="info"
                                        fullWidth
                                        disabled={
                                            loading || !formData.category_id || !formData.title
                                        }
                                    >
                                        {loading ? t("submitInProgress") : t("submitIssue")}
                                    </SoftButton>
                                </form>
                            </SoftBox>
                        </Card>
                    </Grid>

                    {/* ISSUES */}
                    <Grid item xs={12} md={6}>
                        <Card>
                            <SoftBox p={3}>
                                <SoftTypography variant="h5" fontWeight="bold" gutterBottom>
                                    {t("myIssues")}
                                </SoftTypography>

                                {issues.length === 0 ? (
                                    <SoftTypography>{t("noIssues")}</SoftTypography>
                                ) : (
                                    issues.map((issue) => {
                                        const imageFiles =
                                            issue.files?.filter((f) =>
                                                f.file_type.startsWith("image/")
                                            ) || [];
                                        const nonImageFiles =
                                            issue.files?.filter(
                                                (f) => !f.file_type.startsWith("image/")
                                            ) || [];

                                        return (
                                            <SoftBox
                                                key={issue.id}
                                                mb={2}
                                                p={2}
                                                bgColor="grey-100"
                                                borderRadius="lg"
                                            >
                                                <Box
                                                    display="flex"
                                                    flexDirection={{ xs: "column", md: "row" }}
                                                    gap={2}
                                                >
                                                    <Box flex={1}>
                                                        <Box
                                                            display="flex"
                                                            justifyContent="space-between"
                                                            alignItems="center"
                                                        >
                                                            <Box display="flex" alignItems="center">
                                                                <SoftTypography
                                                                    variant="body1"
                                                                    fontWeight="bold"
                                                                    mr={2}
                                                                >
                                                                    {issue.title}
                                                                </SoftTypography>
                                                                <StatusBadge status={issue.status} t={t} />
                                                            </Box>
                                                        </Box>

                                                        <Box display="flex" gap={2} mt={1}>
                                                            <SoftTypography variant="caption" color="text">
                                                                📂{" "}
                                                                {issue.category
                                                                    ? issue.category.charAt(0).toUpperCase() + issue.category.slice(1)
                                                                    : "No Category"}
                                                            </SoftTypography>
                                                            <SoftTypography variant="caption" color="text">
                                                                📅{" "}
                                                                {new Date(
                                                                    issue.created_at
                                                                ).toLocaleDateString()}
                                                            </SoftTypography>
                                                        </Box>

                                                        <SoftTypography variant="body2" mt={1}>
                                                            {issue.description}
                                                        </SoftTypography>
                                                    </Box>

                                                    {imageFiles.length > 0 && <Box
                                                        flexShrink={0}
                                                        minWidth="200px"
                                                        display="flex"
                                                        flexDirection="column"
                                                        gap={1}
                                                    >
                                                        <SoftTypography
                                                            variant="subtitle2"
                                                            fontWeight="medium"
                                                        >
                                                            📎 {t("attachments")}
                                                        </SoftTypography>

                                                        {nonImageFiles.map((file, idx) => (
                                                            <a
                                                                key={idx}
                                                                href={file.file_path}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{
                                                                    color: "#007bff",
                                                                    fontSize: "14px",
                                                                    textDecoration: "none",
                                                                }}
                                                            >
                                                                📄 {t("viewAttachment")} {idx + 1}
                                                            </a>
                                                        ))}

                                                        {/* Thumbnails */}
                                                        {imageFiles.length > 0 && (
                                                            <Box display="flex" gap={1}>
                                                                {imageFiles.slice(0, 3).map((file, idx) => (
                                                                    <Box key={idx} position="relative">
                                                                        <img
                                                                            src={file.file_path}
                                                                            alt={`img-${idx}`}
                                                                            onClick={() =>
                                                                                handleImageClick(
                                                                                    imageFiles.map((f) => f.file_path),
                                                                                    idx
                                                                                )
                                                                            }
                                                                            style={{
                                                                                width: "70px",
                                                                                height: "70px",
                                                                                objectFit: "cover",
                                                                                borderRadius: "8px",
                                                                                cursor: "pointer",
                                                                                border: "1px solid #ccc",
                                                                            }}
                                                                        />
                                                                        {idx === 2 &&
                                                                            imageFiles.length > 3 && (
                                                                                <Box
                                                                                    onClick={() =>
                                                                                        handleImageClick(
                                                                                            imageFiles.map(
                                                                                                (f) => f.file_path
                                                                                            ),
                                                                                            idx
                                                                                        )
                                                                                    }
                                                                                    position="absolute"
                                                                                    top={0}
                                                                                    left={0}
                                                                                    width="70px"
                                                                                    height="70px"
                                                                                    display="flex"
                                                                                    alignItems="center"
                                                                                    justifyContent="center"
                                                                                    bgcolor="rgba(0,0,0,0.6)"
                                                                                    borderRadius="8px"
                                                                                    color="#fff"
                                                                                    fontWeight="bold"
                                                                                    sx={{ cursor: "pointer" }}
                                                                                >
                                                                                    +{imageFiles.length - 3}
                                                                                </Box>
                                                                            )}
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>}
                                                    <Box
                                                        onClick={() => setDeleteConfirm({ open: true, issueId: issue.id, issueTitle: issue.title })}
                                                        sx={{ cursor: "pointer", color: "#D32F2F", display: "flex", alignItems: "center" }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </Box>
                                                </Box>
                                            </SoftBox>
                                        );
                                    })
                                )}

                                {issues.length > 0 && (
                                    <SoftBox
                                        mt={2}
                                        display="flex"
                                        justifyContent="flex-end"
                                        alignItems="center"
                                        gap={2}
                                        width="100%"
                                    >
                                        <SoftBox width="60px">
                                            <Select
                                                id="rowsPerPage"
                                                fullWidth
                                                size="small"
                                                value={pageSize}
                                                onChange={(e) => {
                                                    setPageSize(e.target.value);
                                                    setPage(1);
                                                }}
                                                sx={{
                                                    height: "36px",
                                                    fontSize: "13px",
                                                    border: "1px solid #d2d6da",
                                                    borderRadius: "0.5rem",
                                                    backgroundColor: "#fff",

                                                    "& .MuiSelect-select": {
                                                        padding: "8px 10px",
                                                    },

                                                    "& fieldset": {
                                                        border: "none",
                                                    },
                                                }}
                                                inputProps={{ "aria-label": "Without label" }}
                                            >
                                                <MenuItem value={3}>3</MenuItem>
                                                <MenuItem value={6}>6</MenuItem>
                                                <MenuItem value={12}>12</MenuItem>
                                            </Select>
                                        </SoftBox>

                                        <Pagination
                                            count={totalPages}
                                            page={page}
                                            onChange={(e, value) => setPage(value)}
                                            color="primary"
                                        />
                                    </SoftBox>
                                )}
                            </SoftBox>
                        </Card>
                    </Grid>
                </Grid>
            </SoftBox>

            <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, issueId: null, issueTitle: "" })} maxWidth="xs" fullWidth PaperProps={{ style: { borderRadius: 15 } }}>
                <DialogTitle>
                    {t("deleteDialogTitle")}
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ padding: "10px", textAlign: "center" }}>
                    <MdWarning size={80} style={{ color: "#D32F2F" }} />
                    <SoftTypography component="div" variant="h6" style={{ marginTop: "20px", marginBottom: "10px" }}>
                        {t("deleteDialogWarning")}
                    </SoftTypography>
                    <SoftTypography component="div" variant="body2" color="secondary" style={{ fontSize: "16px" }}>
                        {deleteConfirm.issueTitle}
                    </SoftTypography>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ gap: 1, padding: "8px 24px" }}>
                    <SoftButton variant="gradient" color="secondary" size="medium" onClick={() => setDeleteConfirm({ open: false, issueId: null, issueTitle: "" })}>
                        {t("cancel")}
                    </SoftButton>
                    <SoftButton variant="contained" color="primary" size="medium" onClick={handleDeleteIssue}>
                        {t("delete")}
                    </SoftButton>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(openImage)} onClose={handleCloseDialog} maxWidth="md">
                <DialogContent style={{ padding: 0 }}>
                    {imageList.length > 0 && (
                        <Box display="flex" flexDirection="column" alignItems="center">
                            <img
                                src={imageList[currentImageIndex]}
                                alt="full"
                                style={{ width: "100%", maxHeight: "80vh", objectFit: "contain" }}
                            />
                            <Box mt={2} display="flex" gap={2} mb={2}>
                                <SoftButton
                                    variant="gradient"
                                    disabled={currentImageIndex === 0}
                                    onClick={() => {
                                        const newIndex = currentImageIndex - 1;
                                        setCurrentImageIndex(newIndex);
                                        setOpenImage(imageList[newIndex]);
                                    }}
                                >
                                    <ArrowBackIcon /> Prev
                                </SoftButton>
                                <SoftButton
                                    variant="gradient"
                                    disabled={currentImageIndex === imageList.length - 1}
                                    onClick={() => {
                                        const newIndex = currentImageIndex + 1;
                                        setCurrentImageIndex(newIndex);
                                        setOpenImage(imageList[newIndex]);
                                    }}
                                >
                                    Next <ArrowForwardIcon />
                                </SoftButton>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <SnackbarNotification
                notification={notification}
                setNotification={setNotification}
            />
        </DashboardLayout>
    );
};

export default ReportIssues;